import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Save, User, DollarSign, Package, CheckCircle, ArrowLeft, ChevronDown } from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { CreateVendaRequest } from '../types/api';
import { useCreateVenda } from '../hooks/useApi';
import FadeInContent from '../components/FadeInContent';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(100vh - 120px);
  padding: ${props => props.theme.spacing[6]};
  background-color: ${props => props.theme.colors.background};
`;

const PageHeader = styled.div`
  width: 100%;
  max-width: 760px;
  margin-bottom: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[4]};
`;

const BackButton = styled.button`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  padding: ${props => props.theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  width: 100%;
  max-width: 760px;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  position: relative;
  overflow: visible;
`;

const CardHeader = styled.div`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const CardTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const CardBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const TallCardBody = styled(CardBody)`
  min-height: clamp(240px, 34vh, 360px);
  justify-content: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: clamp(220px, 30vh, 320px);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    min-height: auto;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[4]};
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const TallFormGroup = styled(FormGroup)`
  min-height: clamp(180px, 30vh, 260px);
  justify-content: flex-start;
  gap: ${props => props.theme.spacing[4]};
  padding-bottom: ${props => props.theme.spacing[2]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: clamp(160px, 26vh, 220px);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    min-height: auto;
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${(props: { theme: DefaultTheme }) => props.theme.typography.fontSize.sm};
  font-weight: ${(props: { theme: DefaultTheme }) => props.theme.typography.fontWeight.medium};
  color: ${(props: { theme: DefaultTheme }) => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${(props: { theme: DefaultTheme }) => props.theme.spacing[2]};
  ${(props: { theme: DefaultTheme; $required?: boolean }) => props.$required ? `&::after { content: ' *'; color: ${props.theme.colors.error}; }` : ''}
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }
  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.error};
  margin-top: ${props => props.theme.spacing[1]};
`;

const SelectorContainer = styled.div`
  position: relative;
  display: block;
  z-index: 100;
  width: 100%;
`;

const SelectorButton = styled(motion.button) <{ $active?: boolean; $error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme, $active, $error }) =>
    $error ? theme.colors.error : ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }

  svg:last-child {
    transition: transform 0.3s ease;
    ${({ $active }) => $active && 'transform: rotate(180deg);'}
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background: ${({ theme }) =>
    theme.isLight
      ? '#ffffff'
      : 'rgba(25, 25, 25, 0.98)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.isLight ? theme.colors.border : 'rgba(255, 255, 255, 0.08)'};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  padding: 6px;
  z-index: 101;
`;

const DropdownOption = styled.button<{ $isActive: boolean }>`
  width: 100%;
  padding: 12px 16px;
  background: ${({ $isActive, theme }) =>
    $isActive ? (theme.isLight ? 'rgba(255, 59, 48, 0.08)' : 'rgba(255, 59, 48, 0.15)') : 'transparent'};
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  color: ${({ $isActive, theme }) => $isActive ? theme.colors.error : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ $isActive }) => $isActive ? '600' : '400'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${({ $isActive, theme }) =>
    $isActive ? (theme.isLight ? 'rgba(255, 59, 48, 0.08)' : 'rgba(255, 59, 48, 0.15)') : (theme.isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)')};
    color: ${({ $isActive, theme }) => $isActive ? theme.colors.error : theme.colors.textPrimary};
  }
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[3]};
  width: 100%;
  max-width: 760px;
  margin-top: ${props => props.theme.spacing[2]};
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  ${(props: { theme: DefaultTheme; $variant?: 'primary' | 'secondary' | 'danger' }) => {
    if (props.$variant === 'primary') {
      return `
        background: ${props.theme.colors.primary};
        color: ${props.theme.colors.textPrimary};
        &:hover {
          transform: translateY(-1px);
          box-shadow: ${props.theme.shadows.neon};
          background: ${props.theme.colors.primaryHover};
        }
      `;
    }
    return `
      background: ${props.theme.colors.backgroundSecondary};
      color: ${props.theme.colors.textSecondary};
      border-color: ${props.theme.colors.border};
      &:hover {
        background: ${props.theme.colors.backgroundTertiary};
        color: ${props.theme.colors.textPrimary};
        border-color: ${props.theme.colors.primary};
      }
    `;
  }}
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const statusOptions = [
  { value: 'pago', label: 'Pago' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'cancelada', label: 'Cancelada' },
];

const NewSalePage: React.FC = () => {
  const navigate = useNavigate();
  const createVendaMutation = useCreateVenda();
  const [formData, setFormData] = useState<CreateVendaRequest>({
    cliente: '',
    produto: '',
    valor: '',
    status: 'pendente'
  }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement | null>(null);
  const clienteRef = useRef<HTMLInputElement | null>(null);
  const produtoRef = useRef<HTMLInputElement | null>(null);
  const valorRef = useRef<HTMLInputElement | null>(null);
  const statusButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const theme = useTheme();

  const focusNext = (current: Element | null) => {
    if (!formRef.current) return false;
    const focusable = Array.from(
      formRef.current.querySelectorAll<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => el.offsetParent !== null); // visible

    if (focusable.length === 0) return false;

    const idx = focusable.findIndex((el) => el === current);
    const next = idx >= 0 && idx < focusable.length - 1 ? focusable[idx + 1] : null;
    if (next) {
      next.focus();
      return true;
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (e.shiftKey) return;
    const target = e.target as Element;
    e.preventDefault();
    const moved = focusNext(target);
    if (!moved) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      formRef.current?.dispatchEvent(submitEvent);
    }
  };

  const onClienteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      produtoRef.current?.focus();
    }
  };

  const onProdutoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      valorRef.current?.focus();
    }
  };

  const onValorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      statusButtonRef.current?.focus();
      setDropdownOpen(true);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.cliente.trim()) newErrors.cliente = 'Cliente é obrigatório';
    if (!formData.produto.trim()) newErrors.produto = 'Produto é obrigatório';
    if (!formData.valor.trim() || isNaN(Number(formData.valor.replace(',', '.')))) newErrors.valor = 'Valor inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await createVendaMutation.mutateAsync(formData);
      navigate('/sales');
    } catch (error) {
      console.error('Erro ao criar venda:', error);
    }
  };

  const handleInputChange = (field: keyof CreateVendaRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <FadeInContent>
      <PageContainer>
        <PageHeader>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </BackButton>
          <PageTitle>Criar Nova Venda</PageTitle>
        </PageHeader>

        <Form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          <Card>
            <CardHeader>
              <User size={20} />
              <CardTitle>Informações do Cliente e Produto</CardTitle>
            </CardHeader>
            <CardBody>
              <FormRow>
                <FormGroup>
                  <Label $required>Cliente</Label>
                  <Input
                    type="text"
                    ref={clienteRef}
                    value={formData.cliente}
                    onChange={(e) => handleInputChange('cliente', e.target.value)}
                    onKeyDown={onClienteKeyDown}
                    placeholder="Nome do cliente"
                    $error={!!errors.cliente}
                  />
                  {errors.cliente && <ErrorMessage>{errors.cliente}</ErrorMessage>}
                </FormGroup>
                <FormGroup>
                  <Label $required>Produto</Label>
                  <Input
                    type="text"
                    ref={produtoRef}
                    value={formData.produto}
                    onChange={(e) => handleInputChange('produto', e.target.value)}
                    onKeyDown={onProdutoKeyDown}
                    placeholder="Nome do produto"
                    $error={!!errors.produto}
                  />
                  {errors.produto && <ErrorMessage>{errors.produto}</ErrorMessage>}
                </FormGroup>
              </FormRow>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <DollarSign size={20} />
              <CardTitle>Detalhes da Venda</CardTitle>
            </CardHeader>
            <TallCardBody>
              <FormRow>
                <TallFormGroup>
                  <Label $required>Valor</Label>
                  <Input
                    type="text"
                    ref={valorRef}
                    value={formData.valor}
                    onChange={(e) => handleInputChange('valor', e.target.value.replace(/[^0-9,.]/g, ''))}
                    onKeyDown={onValorKeyDown}
                    placeholder="0,00"
                    $error={!!errors.valor}
                  />
                  {errors.valor && <ErrorMessage>{errors.valor}</ErrorMessage>}
                </TallFormGroup>
                <TallFormGroup>
                  <Label>Status</Label>
                  <SelectorContainer>
                    <SelectorButton
                      type="button"
                      ref={statusButtonRef}
                      onClick={() => setDropdownOpen((open) => !open)}
                      $active={dropdownOpen}
                      whileTap={{ scale: 0.995 }}
                    >
                      <span style={{ flex: 1 }}>
                        {statusOptions.find((opt) => opt.value === formData.status)?.label || 'Selecione'}
                      </span>
                      <ChevronDown size={18} style={{ opacity: 0.5 }} />
                    </SelectorButton>

                    <AnimatePresence>
                      {dropdownOpen && (
                        <>
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setDropdownOpen(false)}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 99
                            }}
                          />
                          <DropdownMenu
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                          >
                            {statusOptions.map((opt) => (
                              <DropdownOption
                                key={opt.value}
                                $isActive={formData.status === opt.value}
                                onClick={() => {
                                  handleInputChange('status', opt.value);
                                  setDropdownOpen(false);
                                }}
                              >
                                {opt.label}
                                {formData.status === opt.value && (
                                  <div style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: theme.colors.error,
                                    boxShadow: `0 0 8px ${theme.colors.error}88`
                                  }} />
                                )}
                              </DropdownOption>
                            ))}
                          </DropdownMenu>
                        </>
                      )}
                    </AnimatePresence>
                  </SelectorContainer>
                </TallFormGroup>
              </FormRow>
            </TallCardBody>
          </Card>

          <FormFooter>
            <Button type="button" onClick={() => navigate('/sales')}>
              Cancelar
            </Button>
            <Button
              type="submit"
              $variant="primary"
              onClick={handleSubmit}
              disabled={createVendaMutation.isPending}
            >
              <Save size={16} />
              {createVendaMutation.isPending ? 'Salvando...' : 'Criar Venda'}
            </Button>
          </FormFooter>
        </Form>
      </PageContainer>
    </FadeInContent>
  );
};

export default NewSalePage;
